import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import './avatarly.scss';
import { Circle } from 'react-feather';
import Image from '../image/image';
import Icon, { IconSizes } from '../icon';
import Tooltip from '../tooltip';
import LinkView from './views/link-view/link-view';

const AvatarlyThemes = {
  LIGHT: 'light',
  DARK: 'dark',
};

const AvatarlySizes = {
  SMALL: 'size-s',
  MEDIUM: 'size-m',
  LARGE: 'size-l',
};

const AvatarlyActivityStatuses = {
  ACTIVE: 'ai-active',
  INACTIVE: 'ai-inactive',
};

const AvatarlyImage = ({
  image, renderIcon, initials, text, size, activity,
}) => {
  const renText = () => {
    if (initials?.length > 2) {
      const mid = Math.round(initials.length / 2);
      return (
        <div className="avatarly-initials ai-len-greater-than-2">
          <span>{initials.slice(0, mid)}</span>
          <span>{initials.slice(mid, initials.length)}</span>
        </div>
      );
    }
    return (
      <div className="avatarly-initials">
        {initials || `${text.split(' ')[0]} ${text.split(' ')[1] || ''}`}
      </div>
    );
  };
  return (
    <div className={clsx('avatarly-image-cont', { 'ai-show-icon': renderIcon })}>
      <Image src={image} alt="avatarly-image" className="avatarly-image" />
      {
        renderIcon
          ? (
            <span className={clsx('avatarly-icon', size)}>
              {renderIcon({ type: renderIcon, size })}
            </span>
          )
          : null
      }
      {renText()}
      {!!activity && (
        <Icon
          className={clsx('ai-activity-icon', activity)}
          icon={Circle}
          size={AvatarlySizes.SMALL}
        />
      )}
    </div>
  );
};

AvatarlyImage.propTypes = {
  renderIcon: PropTypes.func,
  initials: PropTypes.string,
  text: PropTypes.string,
  image: PropTypes.string,
  size: PropTypes.oneOf(Object.values(AvatarlySizes)),
  activity: PropTypes.oneOf(Object.values(AvatarlyActivityStatuses)),
};

AvatarlyImage.defaultProps = {
  renderIcon: null,
  initials: '',
  text: '',
  image: '',
  size: AvatarlySizes.MEDIUM,
  activity: '',
};

const Avatarly = React.forwardRef(({
  initials, text, supportingText,
  image, renderIcon, theme, size,
  showImage, actionIcons, onAction, link,
  activity, hardRedirect,
}, ref) => {
  const getActionIcons = () => {
    if (actionIcons?.length) {
      return (
        <div className="avatarly-action-icons">
          {actionIcons?.map(({
            id, description, renderIcon: renderActionIcons,
          }) => (
            <Tooltip description={description} key={id}>
              { renderActionIcons
                ? (
                  <span className="avatarly-action-icon" onClick={() => onAction(id)}>
                    {renderActionIcons({ size: IconSizes.MEDIUM })}
                  </span>
                )
                : null }
            </Tooltip>
          ))}
        </div>
      );
    }
    return null;
  };

  const getLink = () => (
    <LinkView link={link} text={text} onClick={onAction} hardRedirect={hardRedirect} />
  );

  const linkClass = link && 'with-link';
  return (
    <div ref={ref} className={clsx('avatarly-cont', size, theme, linkClass, { 'avatarly-w-img': showImage })}>
      {showImage ? (
        <AvatarlyImage
          renderIcon={renderIcon}
          initials={initials}
          text={text}
          image={image}
          size={size}
          activity={activity}
        />
      ) : null}
      <div className="avatarly-labels">
        {
          link
            ? getLink()
            : <span className="avatarly-pri-text">{text}</span>
        }
        {supportingText && (
          <div className="avatarly-sec-text">{supportingText}</div>
        )}
      </div>
      {getActionIcons()}
    </div>
  );
});
Avatarly.propTypes = {
  image: PropTypes.string,
  showImage: PropTypes.bool,
  theme: PropTypes.oneOf(Object.values(AvatarlyThemes)),
  size: PropTypes.oneOf(Object.values(AvatarlySizes)),
  renderIcon: PropTypes.func,
  initials: PropTypes.string,
  text: PropTypes.string,
  supportingText: PropTypes.string,
  actionIcons: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    icon: PropTypes.func,
  })),
  onAction: PropTypes.func,
  link: PropTypes.string,
  activity: PropTypes.oneOf(Object.values(AvatarlyActivityStatuses)),
  hardRedirect: PropTypes.bool,
};

Avatarly.defaultProps = {
  renderIcon: null,
  initials: '',
  text: '',
  supportingText: '',
  image: '',
  actionIcons: [],
  onAction: () => { },
  showImage: true,
  theme: AvatarlyThemes.DARK,
  size: AvatarlySizes.MEDIUM,
  link: '',
  activity: null,
  hardRedirect: false,
};

export {
  Avatarly as default,
  AvatarlyImage,
  AvatarlyThemes,
  AvatarlySizes,
  AvatarlyActivityStatuses,
};
